import U from './utils';

const PREFIX = 'listReturnV1_';
const BACK_NAV_ID = 'listBackNavItem';

/**
 * Persist list UI state before navigating to a detail page. Consumed by take() on the view.
 * @param {string} detailPageName - e.g. 'ViewApplication'
 * @param {{ listPage: string, state: object }} payload
 */
export function save(detailPageName, { listPage, state }) {
  try {
    sessionStorage.setItem(
      `${PREFIX}${detailPageName}`,
      JSON.stringify({ listPage, state })
    );
  } catch (e) {
    // ignore quota / private mode
  }
}

/**
 * Read and remove stored list state for a detail page.
 * @param {string} detailPageName
 * @returns {{ listPage: string, state: object } | null}
 */
export function take(detailPageName) {
  const key = `${PREFIX}${detailPageName}`;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    sessionStorage.removeItem(key);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.listPage !== 'string' || !parsed.state) return null;
    return parsed;
  } catch (e) {
    try {
      sessionStorage.removeItem(key);
    } catch (e2) {}
    return null;
  }
}

/**
 * For ViewApplication / ViewUser: add "back to list" as the first #sideNav item, same
 * `.tablinks` styling as the real tabs. Call after comp.sideNav(), before O.changeTab().
 * (Does not use an extra row above the grid — avoids layout side effects.)
 * @param {string} label
 * @param {{ listPage: string, state: object } | null} taken
 */
export function prependBackToSideNav(label, taken) {
  if (!taken || !taken.listPage || !taken.state) return;
  const sideNav = U.id('sideNav');
  if (!sideNav || sideNav.querySelector('[data-list-back-nav="1"]')) return;
  const div = document.createElement('div');
  div.setAttribute('data-list-back-nav', '1');
  div.id = BACK_NAV_ID;
  div.className = 'tablinks';
  div.setAttribute('role', 'button');
  div.textContent = label.startsWith('←') ? label : `← ${label}`;
  div.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    O.PREP(taken.listPage, { listRestore: taken.state });
  });
  sideNav.insertBefore(div, sideNav.firstChild);
}

/**
 * For pages without the tabbed side nav (e.g. ViewOrganisation): a bar under the header
 * Renders after a tick so it survives `renderPage` (shadow → mainContent move).
 */
export function mountBackToListBar(label, taken) {
  if (!taken || !taken.listPage || !taken.state) return;
  setTimeout(() => {
    const main = U.id('mainContent');
    if (!main) return;
    if (main.querySelector('[data-list-back="1"]')) return;
    const wrap = document.createElement('div');
    wrap.setAttribute('data-list-back', '1');
    wrap.className = 'flex items-center gap-2 mb-4 border-b border-gray-200 py-2 px-4';
    const link = document.createElement('span');
    link.className = 'text-primary cursor-pointer text-sm font-medium';
    link.setAttribute('role', 'link');
    link.textContent = label.startsWith('←') ? label : `← ${label}`;
    link.setAttribute('aria-label', 'Back to list');
    link.addEventListener('click', () => {
      O.PREP(taken.listPage, { listRestore: taken.state });
    });
    wrap.appendChild(link);
    main.insertBefore(wrap, main.firstChild);
  }, 0);
}

export default { save, take, prependBackToSideNav, mountBackToListBar };
